import { useCallback, useEffect, useState } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import QuestionForm, {
  mapToAliasPairs,
  type QuestionFormSubmitPayload,
  type QuestionFormValues,
} from './QuestionForm';
import {
  getQuestion,
  toggleQuestionActive,
  updateQuestion,
} from '../services/questions';
import { ApiError } from '@admin/services/api';
import { useToast } from '@admin/stores/toastStore';
import ConfirmModal from '@admin/components/ConfirmModal';
import AssistPanel from './AssistPanel';
import type { QuestionListItem } from '@games/word-clash/shared/types';

function toFormValues(q: QuestionListItem): QuestionFormValues {
  return {
    type: q.type,
    difficulty: q.difficulty,
    category: q.category ?? 'general',
    promptEn: q.promptEn,
    promptTr: q.promptTr,
    validAnswers: [...q.validAnswers],
    validAnswersTr: [...q.validAnswersTr],
    specialAnswers: [...q.specialAnswers],
    specialAnswersTr: [...q.specialAnswersTr],
    plausibleWrongAnswers: [...q.plausibleWrongAnswers],
    plausibleWrongAnswersTr: [...q.plausibleWrongAnswersTr],
    exampleHint: q.exampleHint ?? '',
    exampleHintTr: q.exampleHintTr ?? '',
    aliases: mapToAliasPairs(q.answerAliases),
    aliasesTr: mapToAliasPairs(q.answerAliasesTr),
    contentFlags: [...q.contentFlags],
  };
}

/**
 * Edit-question route. Fetches the target question on mount (via the
 * recent-list fallback in `getQuestion`), pre-fills the form, and saves
 * changes via PATCH. Active/Inactive is a dedicated toggle with a confirm
 * modal — it's the soft-delete affordance since no DELETE endpoint exists.
 */
export default function QuestionEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  // The list passes its active filter query in navigation state so we can
  // return to the filtered list. Falls back to the bare list when the detail
  // is opened directly (deep link, page reload).
  const listSearch = (location.state as { listSearch?: string } | null)
    ?.listSearch;
  const backTo = listSearch ? `/questions?${listSearch}` : '/questions';

  const [loadState, setLoadState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; question: QuestionListItem }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const [confirmingToggle, setConfirmingToggle] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  const fetchQuestion = useCallback(async () => {
    if (!id) {
      setLoadState({ status: 'error', message: 'Missing question id.' });
      return;
    }
    setLoadState({ status: 'loading' });
    try {
      const q = await getQuestion(id);
      setLoadState({ status: 'ready', question: q });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load question';
      setLoadState({ status: 'error', message });
    }
  }, [id]);

  useEffect(() => {
    void fetchQuestion();
  }, [fetchQuestion]);

  async function handleSubmit(payload: QuestionFormSubmitPayload) {
    if (!id) return;
    setSubmitting(true);
    setServerError(null);
    try {
      // `type` is not patchable on the server — omit it rather than send
      // a value that gets silently ignored. Every other field is patchable.
      const updated = await updateQuestion(id, {
        difficulty: payload.difficulty,
        category: payload.category,
        promptEn: payload.promptEn,
        promptTr: payload.promptTr,
        validAnswers: payload.validAnswers,
        validAnswersTr: payload.validAnswersTr,
        specialAnswers: payload.specialAnswers,
        specialAnswersTr: payload.specialAnswersTr,
        plausibleWrongAnswers: payload.plausibleWrongAnswers,
        plausibleWrongAnswersTr: payload.plausibleWrongAnswersTr,
        exampleHint: payload.exampleHint,
        exampleHintTr: payload.exampleHintTr,
        answerAliases: payload.answerAliases,
        answerAliasesTr: payload.answerAliasesTr,
        contentFlags: payload.contentFlags,
      });
      setLoadState({ status: 'ready', question: updated });
      showToast('Question saved', 'success');
      navigate(backTo);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.error}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Failed to save question';
      setServerError(message);
      showToast('Failed to save question', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleActiveConfirmed() {
    if (loadState.status !== 'ready' || !id) return;
    const nextActive = !loadState.question.isActive;
    setTogglingActive(true);
    try {
      const updated = await toggleQuestionActive(id, nextActive);
      setLoadState({ status: 'ready', question: updated });
      showToast(
        nextActive ? 'Question reactivated' : 'Question deactivated',
        'success',
      );
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.error}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Failed to update status';
      showToast(message, 'error');
    } finally {
      setTogglingActive(false);
      setConfirmingToggle(false);
    }
  }

  if (loadState.status === 'loading') {
    return (
      <div data-testid="questions-edit-loading" className="text-sm text-slate-500">
        Loading question…
      </div>
    );
  }

  if (loadState.status === 'error') {
    return (
      <div className="space-y-4" data-testid="questions-edit-error">
        <Link
          to={backTo}
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          ← Back to Questions
        </Link>
        <div
          role="alert"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
        >
          {loadState.message}
        </div>
      </div>
    );
  }

  const question = loadState.question;
  const initial = toFormValues(question);
  const deactivating = question.isActive;

  return (
    <div className="space-y-4" data-testid="questions-edit-screen">
      <div className="flex items-center gap-3">
        <Link
          to={backTo}
          className="text-sm text-slate-600 hover:text-slate-900"
          data-testid="questions-edit-back"
        >
          ← Back to Questions
        </Link>
      </div>
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold text-slate-900">
          Edit question
        </h1>
        <span
          data-testid="questions-edit-active-status"
          className={
            'text-xs px-2 py-0.5 rounded uppercase tracking-wide ' +
            (question.isActive
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-slate-200 text-slate-600')
          }
        >
          {question.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      <QuestionForm
        initial={initial}
        typeLocked
        onSubmit={handleSubmit}
        submitLabel="Save changes"
        submitting={submitting}
        serverError={serverError}
        renderAssistant={(ctx) => <AssistPanel {...ctx} />}
        extraActions={
          <button
            type="button"
            onClick={() => setConfirmingToggle(true)}
            disabled={submitting || togglingActive}
            data-testid="questions-edit-toggle-active"
            className={
              'text-sm rounded px-3 py-2 border disabled:opacity-50 ' +
              (deactivating
                ? 'border-red-300 text-red-700 hover:bg-red-50'
                : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50')
            }
          >
            {deactivating ? 'Deactivate' : 'Reactivate'}
          </button>
        }
      />

      {/* ConfirmModal below */}
      <ConfirmModal
        open={confirmingToggle}
        danger={deactivating}
        title={
          deactivating
            ? 'Deactivate this question?'
            : 'Reactivate this question?'
        }
        description={
          deactivating
            ? 'It will no longer be served to players. You can reactivate it later.'
            : 'It will become eligible for matchmaking again.'
        }
        confirmLabel={deactivating ? 'Deactivate' : 'Reactivate'}
        cancelLabel="Cancel"
        onCancel={() => setConfirmingToggle(false)}
        onConfirm={handleToggleActiveConfirmed}
        busy={togglingActive}
      />
    </div>
  );
}

